import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Collapse,
  Form,
  Label,
  Input,
  Container,
  Row,
  Col,
  Button,
} from 'reactstrap';

import { trackEvent, TRACKS_THIRD_ARM } from 'src/util/segment-storeless';
import isSmsAllowedDuringTrial from 'src/util/phone-codes';
import ToggleStep from './ToggleStep';
import styles from './style.module.css';

const NOTIFICATION_STATUSES = {
  idle: 'idle',
  inProgress: 'inProgress',
  complete: 'complete',
  error: 'error',
};

const NotificationStep = (props) => {
  const isPrefillPhoneOrCountryCodeEmpty = props.prefillPhone === '' || props.prefillCountryCode === '';
  const isPrefillEmailEmpty = props.prefillEmail === '';

  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState(props.prefillEmail);
  const [phone, setPhone] = useState(props.prefillPhone);
  const [countryCode, setCountryCode] = useState(props.prefillCountryCode
    ? props.prefillCountryCode.toString() : '1');
  const [isAlertingViaEmail, setAlertingViaEmail] = useState(true);
  const [isAlertingViaVoice, setAlertingViaVoice] = useState(true);
  const [isAlertingViaSMS, setAlertingViaSMS] = useState(true);
  const [isEmailInError, setIsEmailInError] = useState(isPrefillEmailEmpty);
  const [isPhoneInError, setIsPhoneInError] = useState(isPrefillPhoneOrCountryCodeEmpty);
  const [isOneContactMethodSelected, setIsOneContactMethodSelected] = useState(true);
  const [notificationStatus, setNotificationStatus] = useState(NOTIFICATION_STATUSES.idle);
  const [isSMSAvailable, setSMSAvailable] = useState(true);

  const collapseStyles = isOpen ? styles.stepCollapse : null;
  const isIncidentBeingCreated = notificationStatus === NOTIFICATION_STATUSES.inProgress;
  const isIncidentCreated = notificationStatus === NOTIFICATION_STATUSES.complete;
  const isIncidentOrContactResultedInError = notificationStatus === NOTIFICATION_STATUSES.error;
  const isServiceCreated = Boolean(props.service.id);
  const isCreateIncidentButtonDisabled = isIncidentBeingCreated || !isServiceCreated
    || !isOneContactMethodSelected || isEmailInError || isPhoneInError;

  const getIsNotificationsStepComplete = () => (
    Boolean(window.localStorage.getItem(
      'isOnboardingExperimentNotificationsStepComplete'
    ))
  );

  const toggle = () => {
    if (!isOpen && getIsNotificationsStepComplete()) {
      trackEvent(
        TRACKS_THIRD_ARM.see_step_again,
        props.getSegmentProps({
          step_name: 'notification'
        })
      );
    }
    setIsOpen(!isOpen);
  };

  const createIncidentButtonText = () => {
    if (isIncidentBeingCreated) {
      return 'Creating Incident...';
    }
    if (!isServiceCreated) {
      return 'You must create a service to complete this step';
    }
    if (isIncidentCreated) {
      return 'Create another example incident';
    }
    if (!isOneContactMethodSelected) {
      return 'You must select one contact method';
    }
    if (isIncidentOrContactResultedInError) {
      return 'Try again';
    }
    if (isEmailInError || isPhoneInError) {
      return 'Please fill in required fields';
    }
    return 'Save And Send A Test Notification';
  };

  const checkEmailInputsForErrors = (emailText, isAlertingViaEmailUpdate) => {
    if (isAlertingViaEmailUpdate && emailText === '') {
      setIsEmailInError(true);
    } else {
      setIsEmailInError(false);
    }
  };

  const checkPhoneInputsForErrors = (
    phoneText, countryCodeText, isAlertingViaVoiceUpdate, isAlertingViaSMSUpdate
  ) => {
    if ((isAlertingViaVoiceUpdate || isAlertingViaSMSUpdate) && (phoneText === '' || countryCodeText === '')) {
      setIsPhoneInError(true);
    } else {
      setIsPhoneInError(false);
    }
  };

  const checkForOneNotificationMethod = (
    isAlertingViaEmailUpdate, isAlertingViaVoiceUpdate, isAlertingViaSMSUpdate
  ) => {
    if (!(isAlertingViaEmailUpdate || isAlertingViaVoiceUpdate || isAlertingViaSMSUpdate)) {
      setIsOneContactMethodSelected(false);
    } else {
      setIsOneContactMethodSelected(true);
    }
  };

  const handleEmailCheckboxChange = () => {
    checkEmailInputsForErrors(email, !isAlertingViaEmail);
    checkForOneNotificationMethod(!isAlertingViaEmail, isAlertingViaVoice, isAlertingViaSMS);
    setAlertingViaEmail(!isAlertingViaEmail);
  };

  const handleVoiceCheckboxChange = () => {
    checkPhoneInputsForErrors(phone, countryCode, !isAlertingViaVoice, isAlertingViaSMS);
    checkForOneNotificationMethod(isAlertingViaEmail, !isAlertingViaVoice, isAlertingViaSMS);
    setAlertingViaVoice(!isAlertingViaVoice);
  };

  const handleSMSCheckboxChange = () => {
    checkPhoneInputsForErrors(phone, countryCode, isAlertingViaVoice, !isAlertingViaSMS);
    checkForOneNotificationMethod(isAlertingViaEmail, isAlertingViaVoice, !isAlertingViaSMS);
    setAlertingViaSMS(!isAlertingViaSMS);
  };

  const handleEmailChange = (event) => {
    const newEmail = event.target.value;
    checkEmailInputsForErrors(newEmail, isAlertingViaEmail);
    setEmail(newEmail);
  };

  const handlePhoneChange = (event) => {
    // Remove all non digit chars from the phone number
    const newPhone = event.target.value.replace(/\D/g, '');
    checkPhoneInputsForErrors(newPhone, countryCode, isAlertingViaVoice, isAlertingViaSMS);
    setPhone(newPhone);
  };

  const handleCountryCodeChange = (event) => {
    const { value } = event.target;
    const valueHasSymbol = value[0] === '+';
    const formattedCountryCode = valueHasSymbol ? value.substring(1, 4) : value;

    checkPhoneInputsForErrors(phone, formattedCountryCode, isAlertingViaVoice, isAlertingViaSMS);
    setCountryCode(formattedCountryCode);
    setSMSAvailable(isSmsAllowedDuringTrial(formattedCountryCode));
  };

  const markStepAsComplete = () => {
    window.localStorage.setItem('isOnboardingExperimentNotificationsStepComplete', 'true');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const { user, service } = props;

    // Clear old redux data from previous form submissions
    await props.deleteImmediateNotificationRules(user);
    await props.clearContactMethodsError();
    await props.clearIncidentData();

    setNotificationStatus(NOTIFICATION_STATUSES.inProgress);
    let canContinue = true;

    // update users contact methods
    if (isAlertingViaVoice) {
      canContinue = await props.updateContactIfNeeded(user, 'phone', {
        address: phone,
        countryCode: parseInt(countryCode, 10),
      });
      if (canContinue) await props.createNotificationRule(user, 'phone');
    }
    if (isAlertingViaSMS && canContinue) {
      canContinue = await props.updateContactIfNeeded(user, 'sms', {
        address: phone,
        countryCode: parseInt(countryCode, 10),
      });
      if (canContinue) await props.createNotificationRule(user, 'sms');
    }
    if (isAlertingViaEmail && canContinue) {
      canContinue = await props.updateContactIfNeeded(user, 'email', { address: email });
      if (canContinue) await props.createNotificationRule(user, 'email');
    }

    // create the incident and notification
    if (canContinue) {
      await props.sendNotification(user, service);
    }
  };

  const NotificationStepMarketingCopy = () => (
    <p>
      When an incident occurs, you and your teammates on-call can be notified via email,
      SMS, and/or voice phone call. Complete the form below to indicate your preferred
      notification method(s) and to trigger an example incident.
    </p>
  );

  const NotificationForm = () => (
    <Form onSubmit={handleSubmit} data-test="onboarding-notifications-step-form">
      <Container className="px-4 mt-2">
        <Row className="mb-1">
          <Col xs="3">
            <Container className="h-100 d-flex flex-column">
              <Row className="flex-fill align-items-center">
                <Label check className="font-weight-bold">
                  Email
                </Label>
                <Input
                  type="checkbox"
                  checked={isAlertingViaEmail}
                  onChange={handleEmailCheckboxChange}
                  data-test="onboarding-notifications-step-email-checkbox"
                />
              </Row>
              <Row className="flex-fill align-items-center">
                <Label check className="font-weight-bold">
                  SMS
                </Label>
                <Input
                  type="checkbox"
                  checked={isAlertingViaSMS}
                  onChange={handleSMSCheckboxChange}
                  data-test="onboarding-notifications-step-sms-checkbox"
                />
              </Row>
              <Row className="flex-fill align-items-center">
                <Label check className="font-weight-bold">
                  Voice/Call
                </Label>
                <Input
                  type="checkbox"
                  checked={isAlertingViaVoice}
                  onChange={handleVoiceCheckboxChange}
                  data-test="onboarding-notifications-step-voice-checkbox"
                />
              </Row>
            </Container>
          </Col>
          <Col>
            <Container className="h-100 d-flex flex-column">
              <Row>
                <Label className={isEmailInError ? 'text-danger' : 'text-muted'}>
                  {`Email Address ${isEmailInError ? 'required for email' : ''}`}
                </Label>
              </Row>
              <Row className="flex-fill align-items-center pb-1">
                <Input
                  type="email"
                  name="email-address"
                  aria-label="Email Address"
                  autoComplete="off"
                  className="w-75"
                  value={email}
                  key="email"
                  onChange={handleEmailChange}
                  invalid={isEmailInError}
                  data-test="onboarding-notifications-step-email-input"
                />
              </Row>
              <Row>
                <Label className={isPhoneInError ? 'text-danger' : 'text-muted'}>
                  {`Mobile Number ${isPhoneInError ? 'required for voice and SMS' : ''}`}
                </Label>
              </Row>
              <Row className="flex-fill align-items-center">
                <Input
                  name="area-code"
                  aria-label="Country Code"
                  autoComplete="off"
                  className="w-25"
                  value={countryCode ? `+${countryCode}` : ''}
                  onChange={handleCountryCodeChange}
                  invalid={isPhoneInError}
                  data-test="onboarding-notifications-step-country-code-input"
                />
                <Input
                  type="tel"
                  name="phone"
                  autoComplete="tel"
                  aria-label="Phone Number"
                  className={styles.countryCodeInput}
                  value={phone}
                  onChange={handlePhoneChange}
                  invalid={isPhoneInError}
                  data-test="onboarding-notifications-step-phone-input"
                />
              </Row>
            </Container>
          </Col>
        </Row>
        <Row>
          <Button
            color={isCreateIncidentButtonDisabled ? 'secondary' : 'primary'}
            size="sm"
            className="mb-1 px-2 mx-auto"
            disabled={isCreateIncidentButtonDisabled}
            data-test="onboarding-notifications-step-submit"
          >
            {createIncidentButtonText()}
          </Button>
        </Row>
      </Container>
    </Form>
  );

  const addPeriodIfNecessary = sentence => (
    sentence[sentence.length - 1] === '.' ? sentence : `${sentence}.`
  );

  const IncidentCreatedMessage = () => (
    props.incident.id && isIncidentCreated && (
      <p className="mx-auto text-center text-success" data-test="onboarding-notifications-step-success-message">
        Your example incident has been created. You can click&nbsp;
        <a
          href={`${window.location.origin}/incidents/${props.incident.id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          here
        </a>
        &nbsp;to view it in the browser. You have also been notified via your saved contact methods.
      </p>
    )
  );

  const ErrorMessages = () => (
    <Container>
      {
        props.contactMethodErrors && isIncidentOrContactResultedInError && props.contactMethodErrors
          .map(error => (
            <p className="mx-auto text-danger text-center" key={error}>
              {addPeriodIfNecessary(error)}
            </p>
          ))
      }
      {
        props.incidentError.message && isIncidentOrContactResultedInError && (
          <p className="mx-auto text-danger text-center" data-test="onboarding-notifications-step-error-message">
            {`Your incident has not been created due to: ${props.incidentError.message}`}
          </p>
        )
      }

    </Container>
  );

  const InternationalAlertsCopy = () => (
    <p className="mx-auto text-center text-muted font-weight-bold">
      For your country code, please&nbsp;
      <a
        href={`${window.location.origin}/international_alerts`}
        data-test="onboarding-notifications-step-international-alerts-link"
      >
        submit a ticket
      </a>
      &nbsp;to our support team to enable SMS.
    </p>
  );

  useEffect(() => {
    if (props.incidentError.message || props.contactMethodErrors.length !== 0) {
      setNotificationStatus(NOTIFICATION_STATUSES.error);
    } else if (props.incident.id) {
      markStepAsComplete();
      setNotificationStatus(NOTIFICATION_STATUSES.complete);
    }
  }, [props.incidentError, props.incident, props.contactMethodErrors]);

  useEffect(() => {
    if (notificationStatus === NOTIFICATION_STATUSES.complete) {
      trackEvent(
        TRACKS_THIRD_ARM.notification_sent,
        props.getSegmentProps({
          incident_id: props.incident.id,
          phone_checked: isAlertingViaVoice,
          sms_checked: isAlertingViaSMS,
          email_checked: isAlertingViaEmail,
        })
      );
    }
  }, [notificationStatus]);

  return (
    <div>
      <ToggleStep
        isOpen={isOpen}
        toggle={toggle}
        stepNumber="2"
        stepTitle="Tell us your preferred notification method"
        isCompleted={getIsNotificationsStepComplete()}
      />
      <Collapse className={collapseStyles} isOpen={isOpen} data-test="onboarding-notifications-step-toggle">
        <Container>
          <Row>
            { NotificationStepMarketingCopy() }
          </Row>
          <Row>
            { NotificationForm() }
          </Row>
          <Row>
            { ErrorMessages() }
            { IncidentCreatedMessage() }
          </Row>
          { !isSMSAvailable
            && (
            <Row>
              { InternationalAlertsCopy() }
            </Row>
            )
          }
        </Container>
      </Collapse>
    </div>
  );
};

NotificationStep.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  service: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  deleteImmediateNotificationRules: PropTypes.func.isRequired,
  updateContactIfNeeded: PropTypes.func.isRequired,
  createNotificationRule: PropTypes.func.isRequired,
  sendNotification: PropTypes.func.isRequired,
  prefillEmail: PropTypes.string,
  prefillPhone: PropTypes.string,
  prefillCountryCode: PropTypes.string,
  incident: PropTypes.shape({
    id: PropTypes.string,
  }),
  incidentError: PropTypes.shape({
    message: PropTypes.string
  }),
  contactMethodErrors: PropTypes.arrayOf(PropTypes.string).isRequired,
  clearContactMethodsError: PropTypes.func.isRequired,
  clearIncidentData: PropTypes.func.isRequired,
  getSegmentProps: PropTypes.func.isRequired,
};

NotificationStep.defaultProps = {
  prefillEmail: '',
  prefillPhone: '',
  prefillCountryCode: null,
  incident: {
    id: '',
  },
  incidentError: {
    message: '',
  },
};

export default NotificationStep;
